import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { getActiveCoupons, getUpcomingCoupons } from "../lib/api";
import { couponLabel, couponTitle } from "../lib/coupons";

export default function CouponStrip() {
  const [coupons, setCoupons] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    getActiveCoupons()
      .then((d) => setCoupons(d.coupons || []))
      .catch(() => setCoupons([]));
    getUpcomingCoupons(3)
      .then((d) => setUpcoming(d.coupons || []))
      .catch(() => setUpcoming([]));
  }, []);

  if (!coupons.length && !upcoming.length) return null;

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} copied — paste it at checkout`);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="border-b border-rw-ink/10 bg-rw-mint/70"
      data-testid="coupon-strip"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-3">
        {coupons.length > 0 && <span className="overline">Offers live now</span>}
        {coupons.map((c) => (
          <div key={c.code} className="flex items-center gap-2" data-testid={`coupon-chip-${c.code}`}>
            <button
              onClick={() => copy(c.code)}
              className="rounded-full bg-rw-ink text-white px-3.5 py-1.5 text-xs font-mono font-semibold tracking-wider hover:bg-rw-blue hover:text-rw-ink transition-colors"
              title="Tap to copy"
              data-testid={`coupon-copy-${c.code}`}
            >
              {c.code} · {couponLabel(c)}
            </button>
            <span className="text-xs text-rw-ink2 hidden md:inline">
              {c.description || (c.type === "percent" ? "Percent discount at checkout" : "Flat discount at checkout")}
              {c.first_order_only ? " (first order)" : ""}
              {c.weekends_only ? " · this weekend only" : ""}
              {c.end_date ? ` · ends ${new Date(c.end_date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}
            </span>
            <Link
              to={`/book?coupon=${encodeURIComponent(c.code)}`}
              className="text-xs font-semibold underline underline-offset-2 hover:text-rw-blue"
              data-testid={`coupon-book-${c.code}`}
            >
              Book with {c.code} →
            </Link>
          </div>
        ))}
        {upcoming.map((c) => (
          <div
            key={`up-${c.code}`}
            className="flex items-center gap-2 rounded-full border border-dashed border-rw-ink/30 px-3.5 py-1.5 text-xs"
            data-testid={`coupon-teaser-${c.code}`}
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rw-blue opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-rw-blue" />
            </span>
            <span>
              <strong>{couponTitle(c)}</strong> starts {c.days_until === 1 ? "tomorrow" : `in ${c.days_until} days`}
              <span className="text-rw-ink2"> · {c.code} {couponLabel(c)}</span>
            </span>
          </div>
        ))}
      </div>
    </motion.section>
  );
}
